// /albums/:albumId — single-album view (W3 §6.2).
//
// Sticky glass-2 header (back arrow + cover thumb + name + item count +
// "+ 新增相片" bar button), 3-col photo grid with 8px gap + rounded-md
// tiles.  Tap a tile → PhotoLightbox.  Long-press → ActionSheet for share /
// delete.  A 載入更多 control appears when the album holds more photos than one
// request returned — the endpoint defaults to `limit=50` and W39.53 left the
// other 8 of a 58-photo album unreachable.
//
// W39.53 split two pieces out of this file to stay under the 15 KB route
// ceiling (`AGENTS.md §3 #10`): `components/albums/PhotoTile.tsx` and
// `components/albums/AlbumSettingsRows.tsx`.
//
// Backed by `useAlbumDetail` + `useAlbumItemsPaged(albumId, "taken_at_desc")`.

import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus } from "lucide-react";

import { PageHero } from "@/components/primitives/PageHero";
import { EmptyState } from "@/components/primitives/EmptyState";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { PageContainer, SectionStack } from "@/components/layout";
import { ShareSheet } from "@/components/system";
import { ApiError } from "@/lib/api/pair";
import { useAlbumDetail, useAlbumItemsPaged, useDeleteAlbumItem, useUpdateAlbum } from "@/lib/hooks/album";
import { getPublicUrl } from "@/lib/config";
import { useAlbumList } from "@/lib/hooks/album";
import type { AlbumItemRead } from "@/lib/api/album";
import { PhotoLightbox } from "@/components/albums/PhotoLightbox";
import { PhotoTile } from "@/components/albums/PhotoTile";
import { AlbumSettingsRows } from "@/components/albums/AlbumSettingsRows";
import { AlbumCoverSheet } from "@/components/albums/AlbumCoverSheet";
import { AlbumPhotoActions } from "@/components/albums/AlbumPhotoActions";
import { UploadModal } from "@/components/albums/UploadModal";
import { downloadAlbumPhotos } from "@/lib/albums/downloadAlbumPhotos";

export function AlbumDetailPage() {
  const params = useParams<{ albumId: string }>();
  const navigate = useNavigate();
  const albumId = params.albumId ?? "";

  const albumQuery = useAlbumDetail(albumId);
  const itemsQuery = useAlbumItemsPaged(albumId, "taken_at_desc");
  const deleteItemMut = useDeleteAlbumItem(albumId);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [actionItemId, setActionItemId] = useState<string | null>(null);
  // W39.4 — 「刪除這張」 needs a confirmation step. See the ActionSheet handler
  // below for why.
  const [pendingItemDelete, setPendingItemDelete] = useState<AlbumItemRead | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  // W39.54 — the cover picker, and the one mutation that was already in the
  // module (`useUpdateAlbum`) but had no caller until the server could hold a
  // chosen cover.
  const [coverOpen, setCoverOpen] = useState(false);
  const updateAlbumMut = useUpdateAlbum();
  // W39.53 — 「下載全部」 had no handler at all. Download state is visible
  // because a tap that starts 50 files and then says nothing is indistinguishable
  // from a dead button, which is what it used to be.
  const [downloadState, setDownloadState] = useState<{ done: number; total: number } | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const handleDownloadAll = useCallback(async () => {
    const all = (itemsQuery.data?.pages ?? []).flatMap((p) => p.items);
    if (all.length === 0) return;
    setRowError(null);
    setDownloadState({ done: 0, total: all.length });
    const result = await downloadAlbumPhotos(all, (done) =>
      setDownloadState({ done, total: all.length }),
    );
    setDownloadState(null);
    if (result.failed > 0) {
      setRowError(`有 ${result.failed} 張照片無法下載，請稍後再試。`);
    }
  }, [itemsQuery.data]);

  // W39.54 — the photo sheet's 「設為封面」 takes the same path as the picker, so
  // it lands the user on the grid with the choice already made rather than
  // making them find the photo twice.
  const setCoverAndOpen = useCallback(
    async (mediaId: string) => {
      try {
        await updateAlbumMut.mutateAsync({ albumId, cover_media_id: mediaId });
        setRowError(null);
      } catch (err) {
        setRowError(
          err instanceof ApiError ? `設定封面失敗：${err.message}` : "設定封面失敗，請稍後再試。",
        );
      }
    },
    [albumId, updateAlbumMut],
  );

  const confirmItemDelete = useCallback(async () => {
    const item = pendingItemDelete;
    setPendingItemDelete(null);
    if (!item) return;
    try {
      await deleteItemMut.mutateAsync(item.id);
    } catch (err) {
      console.error("delete album item failed", err);
    }
  }, [deleteItemMut, pendingItemDelete]);

  // All pair albums for the upload-modal chip row (w3.md §6.4).
  const { data: albumsData } = useAlbumList();
  const albumOptions = (albumsData ?? []).map((a) => ({
    id: a.id,
    name: a.name,
    is_system: a.is_system,
  }));

  const handleItemLongPress = useCallback((itemId: string) => {
    setActionItemId(itemId);
  }, []);

  if (albumQuery.isLoading) {
    return (
      <div data-testid="album-detail-page" data-album-id={albumId}>
        <LoadingState count={2} />
      </div>
    );
  }

  // W54.0 B2（第三處）— 讀取失敗不是「找不到這本相簿」。
  // 500 的時候 `data` 是 undefined，於是原本 `isError || !data` 的合併判斷
  // 會把「讀不到」說成「不存在」—— 而相簿其實還在那裡，只是我們讀不到。
  // spec B2 只點名兩處，但同一個缺陷就在同一個檔案的上一段；
  // 規則是「網路錯誤與不存在不得共用一句話」，那就一併分開。
  if (albumQuery.isError) {
    return (
      <div data-testid="album-detail-page" data-album-id={albumId}>
        <PageHero
          crumbs={[
            { to: "/albums", label: "相簿", testId: "album-detail-back" },
            { label: "載入失敗" },
          ]}
          eyebrow="相簿"
          title="相簿載入失敗"
        />
        <ErrorState onRetry={() => void albumQuery.refetch()} />
      </div>
    );
  }

  if (!albumQuery.data) {
    return (
      <div data-testid="album-detail-page" data-album-id={albumId}>
        <PageHero
          crumbs={[
            { to: "/albums", label: "相簿", testId: "album-detail-back" },
            { label: "找不到" },
          ]}
          eyebrow="相簿"
          title="找不到這本相簿"
        />
        <ErrorState
          title="找不到這本相簿"
          description="可能已被刪除，或連結已失效"
          retryLabel="回到相簿列表"
          onRetry={() => navigate("/albums")}
        />
      </div>
    );
  }

  const album = albumQuery.data;
  // W39.53 — pages, not one page. The endpoint defaults to `limit=50` and
  // nothing used to ask for more, so a 58-photo album rendered 50 tiles with no
  // way to reach the rest, while `AlbumRead.item_count` (58) sat right there on
  // the same screen.
  const pages = itemsQuery.data?.pages ?? [];
  const items: AlbumItemRead[] = pages.flatMap((p) => p.items);
  // The server's count, not the length of what happens to be loaded — otherwise
  // the hero would keep agreeing with the screen and disagree with the list.
  const itemsTotal = pages[0]?.total ?? album?.item_count ?? items.length;
  const photoItems: AlbumItemRead[] = items.filter((it) => it.media.mime_type.startsWith("image/"));
  const currentIndex = lightboxIndex ?? 0;
  const actionItem = actionItemId ? (items.find((it) => it.id === actionItemId) ?? null) : null;
  // 「刪除這張」 on a photo with no caption used to open the sheet with a blank
  // header, because the title was `actionItem?.caption ?? ""`. Name the photo by
  // its position instead, so the sheet always says what it is acting on.
  const actionItemIndex = actionItem ? items.findIndex((it) => it.id === actionItem.id) : -1;
  const actionItemTitle =
    actionItem?.caption || (actionItemIndex >= 0 ? `第 ${actionItemIndex + 1} 張照片` : "");

  return (
    <>
      <div data-testid="album-detail-page" data-album-id={album.id}>
        <PageHero
          // W52.0 D-9 — the circular `‹` in `trailing` becomes the trail's
          // parent segment. Eleven pages carried a hand-written back control in
          // four shapes (circle, chip, plain text, bottom-bar arrow); this is
          // the one shape, and it also answers 「你在哪一層」.
          crumbs={[
            { to: "/albums", label: "相簿", testId: "album-detail-back" },
            { label: album.name },
          ]}
          eyebrow={album.is_system ? "系統相簿" : "相簿"}
          title={album.name}
          // W52.0 D-8 — this line used to sit **outside** `PageContainer`, so
          // the cover thumb sat flush against the screen edge (`PageHero` and
          // `PageContainer` each carry `px-s-4`; that row had neither). Deleting
          // it also deletes a **second count**: it read `album.item_count`
          // while the hero read `itemsTotal` from the server total, and those
          // two can disagree.
          subtitle={`${itemsTotal} 張相片${album.item_count > 0 ? ` · ${album.is_system ? "未分類" : album.created_by.display_name}` : ""}`}
          trailing={
            <button
              type="button"
              data-testid="album-detail-upload"
              aria-label="新增相片"
              onClick={() => setUploadOpen(true)}
              className="press cta-pill hover:opacity-90 transition-opacity"
            >
              <Plus size={18} aria-hidden />
              新增相片
            </button>
          }
        />

        <PageContainer vertical="compact">
          <SectionStack density="compact">
            {itemsQuery.isLoading ? (
              <LoadingState count={6} />
            ) : itemsQuery.isError ? (
              // W54.0 B1 — a failed read is not 「還沒有相片」. That empty
              // state does not merely report a wrong fact, it **invites the
              // person to re-upload photos that already exist** — the album's
              // own header says how many there are. The error state says we
              // could not find out, and offers a retry.
              <ErrorState onRetry={() => void itemsQuery.refetch()} />
            ) : photoItems.length === 0 ? (
              <EmptyState
                title="還沒有相片"
                description="點擊 + 新增相片，記錄你們的故事"
                actionLabel="新增相片"
                onAction={() => setUploadOpen(true)}
              />
            ) : (
              <>
                <div className="grid grid-cols-3 gap-1" data-testid="album-photo-grid">
                  {photoItems.map((item, idx) => (
                    <PhotoTile
                      key={item.id}
                      item={item}
                      index={idx}
                      onOpen={(i) => setLightboxIndex(i)}
                      onLongPress={handleItemLongPress}
                    />
                  ))}
                </div>
                {itemsQuery.hasNextPage ? (
                  <button
                    type="button"
                    data-testid="album-load-more"
                    disabled={itemsQuery.isFetchingNextPage}
                    onClick={() => void itemsQuery.fetchNextPage()}
                    className="press mt-3 w-full min-h-[44px] rounded-2xl bg-paper-elevated px-4 py-2 text-body text-ink-1 hover:bg-black/5 transition-colors disabled:opacity-40"
                  >
                    {itemsQuery.isFetchingNextPage
                      ? "載入中…"
                      : `載入更多（已顯示 ${items.length} / ${itemsTotal}）`}
                  </button>
                ) : null}
              </>
            )}

            {rowError ? (
              <p
                className="text-body-sm text-partner-strong"
                data-testid="album-row-error"
                role="alert"
              >
                {rowError}
              </p>
            ) : null}

            <AlbumSettingsRows
              hasPhotos={items.length > 0}
              coverIsManual={album.cover_is_manual}
              downloadState={downloadState}
              onShare={() => setShareOpen(true)}
              onChooseCover={() => setCoverOpen(true)}
              onDownloadAll={() => void handleDownloadAll()}
            />
          </SectionStack>
        </PageContainer>
      </div>

      <AlbumCoverSheet
        open={coverOpen}
        onOpenChange={setCoverOpen}
        items={items}
        currentCoverMediaId={album.cover_media?.id ?? null}
        coverIsManual={album.cover_is_manual}
        busy={updateAlbumMut.isPending}
        onClose={() => setCoverOpen(false)}
        onPick={(mediaId: string) =>
          updateAlbumMut.mutateAsync({ albumId: album.id, cover_media_id: mediaId })
        }
        onUseAutomatic={() =>
          updateAlbumMut.mutateAsync({ albumId: album.id, cover_is_manual: false })
        }
      />

      <PhotoLightbox
        open={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        photos={photoItems.map((it) => ({
          id: it.id,
          media_id: it.media.id,
          url: it.media.url,
          caption: it.caption ?? "",
        }))}
        startIndex={currentIndex}
        title={`${album.name} · ${currentIndex + 1} / ${photoItems.length}`}
      />

      <AlbumPhotoActions
        item={actionItem}
        title={actionItemTitle}
        pendingDelete={pendingItemDelete}
        onCloseSheet={() => setActionItemId(null)}
        onDismissConfirm={() => setPendingItemDelete(null)}
        onRequestDelete={(item) => setPendingItemDelete(item)}
        onShare={() => setShareOpen(true)}
        onSetCover={(item) => void setCoverAndOpen(item.media.id)}
        onConfirmDelete={() => void confirmItemDelete()}
      />

      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`分享「${album.name}」`}
        text={`一起看看我們的「${album.name}」相簿 · ${itemsTotal} 張照片`}
        url={`${getPublicUrl()}/albums/${album.id}`}
        expiresLabel={`最後更新：${new Date(album.updated_at).toLocaleDateString()}`}
        testId="album-share-sheet"
      />

      <UploadModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        defaultAlbumId={album.id}
        albumOptions={albumOptions}
        onUploaded={() => {
          void itemsQuery.refetch();
          void albumQuery.refetch();
        }}
      />
    </>
  );
}

